import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Truck, Loader2, CheckCircle2, XCircle, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Shipping rate estimator - drops in on Product Detail and Checkout pages.
 * Calls POST /api/shiprocket/rates with product IDs + quantities + delivery pincode.
 *
 * Props:
 *   items         - [{product_id, quantity}]
 *   compact       - if true, renders inline (checkout), else full card (product detail)
 *   initialPin    - preset pincode (checkout passes the shipping.postal_code)
 *   pinReadOnly   - if true, pin input is disabled + mirrors initialPin reactively (checkout uses shipping-address pincode only)
 *   selectable    - if true, renders courier options as radios and calls onSelect
 *   onSelect      - callback with {courier_name, rate, eta_days, id, is_cargo} when customer picks a courier
 *   onCheckedChange - callback fired with true/false whenever a valid rate-check result is available (used to enable pay button)
 */
export default function ShippingEstimator({ items = [], compact = false, initialPin = "", pinReadOnly = false, selectable = false, onSelect, onCheckedChange }) {
  const [pin, setPin] = useState(initialPin);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [chosen, setChosen] = useState(null);

  // Reset result whenever the mirrored pincode / items change so the customer must re-check.
  useEffect(() => {
    if (pinReadOnly) setPin(initialPin);
    setResult(null); setChosen(null); setError("");
    onCheckedChange?.(false);
    if (onSelect) onSelect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPin, pinReadOnly, JSON.stringify(items.map((i) => `${i.product_id}:${i.quantity}`))]);

  const check = async () => {
    setError(""); setResult(null); setChosen(null);
    onCheckedChange?.(false);
    if (onSelect) onSelect(null);
    const clean = (pin || "").trim();
    if (!/^\d{6}$/.test(clean)) {
      setError(pinReadOnly ? "Enter the delivery pincode in the shipping address field above." : "Enter a valid 6-digit Indian pincode.");
      return;
    }
    if (!items.length) {
      setError("No items to estimate.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/shiprocket/rates", {
        delivery_pincode: clean,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity || 1 })),
      });
      if (!data.available) {
        setError(data.reason || "Shipping rates not available.");
      } else {
        setResult(data);
        // Auto-select cheapest for immediate customer benefit
        if (selectable && data.options?.length) {
          const first = data.options[0];
          setChosen(first.id);
          onSelect?.(first);
          onCheckedChange?.(true);
        } else if (!selectable) {
          onCheckedChange?.(true);
        }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to fetch rates. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const pick = (opt) => {
    setChosen(opt.id);
    onSelect?.(opt);
    onCheckedChange?.(true);
  };

  return (
    <div className={`${compact ? "" : "border border-slate-200 p-4 bg-white"}`}>
      <div className={`flex items-center gap-2 ${compact ? "mb-2" : "mb-3"}`}>
        <Truck size={16} className="text-tmf-blue shrink-0" />
        <div className="font-display font-bold text-tmf-navy uppercase tracking-widest text-xs">
          {compact ? "Shipping Rate" : "Delivery cost & ETA"}
        </div>
      </div>
      <div className="flex gap-2">
        <Input
          data-testid="ship-est-pincode"
          value={pin}
          onChange={(e) => { if (!pinReadOnly) setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); }}
          placeholder={pinReadOnly ? "Uses shipping-address pincode" : "6-digit pincode"}
          readOnly={pinReadOnly}
          aria-readonly={pinReadOnly}
          className={`rounded-none font-mono-tmf uppercase tracking-widest text-sm ${pinReadOnly ? "bg-slate-100 text-slate-600 cursor-not-allowed" : ""}`}
          maxLength={6}
          onKeyDown={(e) => e.key === "Enter" && !pinReadOnly && (e.preventDefault(), check())}
        />
        <Button
          data-testid="ship-est-check"
          onClick={check}
          disabled={busy}
          className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase text-[10px] font-bold tracking-widest"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Check Shipping Rates"}
        </Button>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-700 font-mono-tmf bg-amber-50 border border-amber-200 p-2">
          <XCircle size={13} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div data-testid="ship-est-result" className="mt-3 space-y-2 text-xs">
          <div className="flex items-center gap-2 text-green-700 font-mono-tmf uppercase tracking-widest text-[10px]">
            <CheckCircle2 size={13} /> Deliverable to {result.delivery_pincode}
          </div>

          {selectable && result.options?.length > 0 ? (
            <div className="space-y-1.5" data-testid="ship-options">
              {result.options.map((opt) => {
                const active = chosen === opt.id;
                const optKey = `${opt.id}-${opt.cargo_mode_id ?? opt.cargo_mode_name ?? "x"}`;
                return (
                  <label
                    key={optKey}
                    data-testid={`ship-opt-${optKey}`}
                    className={`flex items-center gap-3 border p-2.5 cursor-pointer transition ${
                      active ? "border-tmf-blue bg-tmf-blue/5" : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <input type="radio" name="ship-opt" checked={active} onChange={() => pick(opt)} className="accent-tmf-blue" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-tmf-navy uppercase text-[11px] truncate">{opt.courier_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono-tmf">
                        {opt.eta_days ? `~${opt.eta_days} days` : (opt.etd || "")}
                      </div>
                    </div>
                    <div className="font-display font-black text-tmf-navy shrink-0">₹{Math.round(opt.rate || 0)}</div>
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-tmf-blue bg-tmf-blue/5 p-2">
                <div className="text-[9px] font-mono-tmf uppercase tracking-widest text-tmf-blue font-bold">Cheapest</div>
                <div className="font-bold text-tmf-navy uppercase text-[11px]">{result.cheapest?.courier_name}</div>
                <div className="font-display font-black text-lg text-tmf-navy">₹{Math.round(result.cheapest?.rate || 0)}</div>
                <div className="text-[10px] text-slate-600 font-mono-tmf">
                  {result.cheapest?.eta_days ? `~${result.cheapest.eta_days} days` : (result.cheapest?.etd || "")}
                </div>
              </div>
              <div className="border border-slate-300 p-2">
                <div className="text-[9px] font-mono-tmf uppercase tracking-widest text-slate-500 font-bold">Fastest</div>
                <div className="font-bold text-tmf-navy uppercase text-[11px]">{result.fastest?.courier_name}</div>
                <div className="font-display font-black text-lg text-tmf-navy">₹{Math.round(result.fastest?.rate || 0)}</div>
                <div className="text-[10px] text-slate-600 font-mono-tmf">
                  {result.fastest?.eta_days ? `~${result.fastest.eta_days} days` : (result.fastest?.etd || "")}
                </div>
              </div>
            </div>
          )}

          {result.package && (
            <div className="text-[10px] text-slate-500 font-mono-tmf">
              Package: {result.package.weight_kg}kg · {result.package.length_cm}×{result.package.width_cm}×{result.package.height_cm}cm
            </div>
          )}

          <div data-testid="ship-transparency-note" className="mt-2 flex items-start gap-2 text-[10px] leading-relaxed text-slate-600 bg-slate-50 border border-slate-200 p-2">
            <Info size={12} className="shrink-0 mt-0.5 text-tmf-blue" />
            <span><b className="uppercase tracking-widest text-[9px] text-tmf-navy">Note:</b> Delivery charges are auto-calculated by our logistics partner based on your address and order details. We do not charge any additional shipping fees.</span>
          </div>
        </div>
      )}
    </div>
  );
}
